import React, { useMemo } from 'react';
import { ACCENT_HEX, ACCENT_SOFT } from '../../lib/juniorTheme';
import { ECO_SOUND_META, type EcoRitmoExercise } from '../../lib/rhythm/ecoRitmoConfig';
import { micInputLabel, type EcoMicSettings } from '../../lib/rhythm/ecoMicConfig';
import { measureIndexForCell } from '../../lib/rhythm/ecoRitmoConfig';
import type { RhythmAttackEvent } from '../../lib/rhythm/rhythmTiming';
import { RhythmTapEngine } from '../../lib/rhythm/rhythmTapEngine';
import { EcoTrainPlayView } from './EcoTrainPlayView';
import { attacksToCellStates } from './FigureGrid';
import { TapPalmasButton } from './TapPalmasButton';

export type EcoPhase = 'demo' | 'countdown' | 'play' | 'results';

interface EcoRitmoStageProps {
  exercise: EcoRitmoExercise;
  phase: EcoPhase;
  engine: RhythmTapEngine;
  activeCellIndex: number;
  countdown: number;
  score: number;
  passed: boolean;
  faultCount: number;
  tapFlash: 'hit' | 'fault' | null;
  micSettings: EcoMicSettings;
  micActive: boolean;
  micVolume: number;
  micError: string | null;
  presentation?: boolean;
  onTap: () => void;
  onRetry: () => void;
  onBackToLobby: () => void;
}

function attackSummary(attacks: readonly RhythmAttackEvent[]): { hits: number; late: number; miss: number } {
  let hits = 0;
  let late = 0;
  let miss = 0;
  for (const a of attacks) {
    if (a.status === 'hit') hits++;
    else if (a.status === 'late') late++;
    else if (a.status === 'miss') miss++;
  }
  return { hits, late, miss };
}

export const EcoRitmoStage: React.FC<EcoRitmoStageProps> = ({
  exercise,
  phase,
  engine,
  activeCellIndex,
  countdown,
  score,
  passed,
  faultCount,
  tapFlash,
  micSettings,
  micActive,
  micVolume,
  micError,
  presentation = false,
  onTap,
  onRetry,
  onBackToLobby,
}) => {
  const attacks = engine.getAttacks();
  const cellStates =
    phase === 'play' || phase === 'results'
      ? attacksToCellStates(engine.timeline.cells, attacks)
      : undefined;
  const summary = attackSummary(attacks);

  const visibleMeasure = useMemo(() => {
    if (phase === 'results') return 0;
    if (activeCellIndex >= 0) return measureIndexForCell(exercise.pattern, activeCellIndex);
    return 0;
  }, [phase, activeCellIndex, exercise.pattern]);

  const soundMeta = ECO_SOUND_META[exercise.soundId];
  const micUsesMic = micSettings.inputMode !== 'tap';
  const tapEnabled = micSettings.inputMode !== 'mic';

  const playSub =
    micSettings.inputMode === 'mic'
      ? `${soundMeta.label} con micrófono · silencio en descansos`
      : micSettings.inputMode === 'both'
        ? `${soundMeta.label} en pantalla o mic · silencio en descansos`
        : `${soundMeta.label} en sonidos · silencio en descansos`;

  const banner =
    phase === 'results'
      ? { text: passed ? '¡Genial!' : '¡Casi!', sub: undefined as string | undefined }
      : phase === 'countdown'
        ? { text: countdown > 0 ? String(countdown) : '¡Ya!', sub: undefined as string | undefined }
        : phase === 'demo'
          ? { text: 'Escucha…', sub: 'Sigue las figuras' }
          : {
              text: '¡Tu turno!',
              sub: playSub,
            };

  return (
    <div
      className={`flex flex-col flex-1 min-h-0 overflow-hidden gap-2 sm:gap-3 py-1 eco-stage-root${
        presentation ? ' junior-presentation-active' : ''
      }`}
    >
      <div
        className={`text-center shrink-0 eco-stage-root__banner${
          phase === 'countdown' && countdown > 0 ? ' eco-stage-root__banner--countdown' : ''
        }`}
      >
        <p
          className={`junior-display ${
            phase === 'countdown' && countdown > 0 ? 'text-5xl sm:text-6xl eco-countdown-pop' : 'text-xl sm:text-2xl'
          }`}
          style={{ color: '#2A2540' }}
        >
          {banner.text}
        </p>
        {banner.sub && (
          <p
            className="text-xs font-bold mt-0.5 eco-stage-root__banner-sub"
            style={{ color: '#6B6385' }}
          >
            {banner.sub}
          </p>
        )}
        {phase !== 'results' && exercise.measureCount > 1 && (
          <p className="text-[10px] font-extrabold mt-1 tabular-nums" style={{ color: ACCENT_HEX.sky }}>
            Compás {visibleMeasure + 1} / {exercise.measureCount}
          </p>
        )}
      </div>

      <div className="flex-1 min-h-0 flex flex-col items-stretch gap-2 overflow-hidden">
        {phase !== 'results' && (
          <EcoTrainPlayView
            exercise={exercise}
            activeCellIndex={phase === 'countdown' ? -1 : activeCellIndex}
            cellStates={cellStates}
          />
        )}

        {(phase === 'play' || phase === 'countdown') && (
          <div className="shrink-0 flex flex-col items-center gap-1 pb-1">
            {tapEnabled && (
              <TapPalmasButton
                onTap={onTap}
                disabled={phase !== 'play'}
                flash={tapFlash}
                label={soundMeta.label}
                emoji={soundMeta.emoji}
              />
            )}

            {phase === 'play' && micUsesMic && (
              <div className="flex flex-col items-center gap-1 w-full max-w-xs px-2">
                <div
                  className={`eco-mic-meter w-full h-2 rounded-full overflow-hidden ${micActive ? 'eco-mic-meter--on' : ''}`}
                  style={{ ['--vol' as string]: `${Math.round(Math.max(micVolume, 0.02) * 100)}%` }}
                  title={micInputLabel(micSettings.inputMode)}
                  aria-hidden
                />
                <p className="text-[10px] font-bold text-center" style={{ color: '#6B6385' }}>
                  {micActive ? '🎤 Escuchando golpes…' : '🎤 Aplaude o chasquea cerca del mic'}
                  {micSettings.inputMode === 'both' ? ' · o toca el botón' : ''}
                </p>
                {micError && (
                  <p className="text-[10px] font-bold text-center" style={{ color: '#F472B6' }}>
                    {micError}
                  </p>
                )}
              </div>
            )}

            {phase === 'play' && faultCount > 0 && (
              <p className="text-xs font-bold" style={{ color: '#F472B6' }}>
                Faltas: {faultCount}/3
              </p>
            )}
          </div>
        )}

        {phase === 'results' && (
          <div
            className="w-full max-w-sm rounded-[1.5rem] p-5 text-center shrink-0"
            style={{
              background: passed
                ? `linear-gradient(160deg, ${ACCENT_SOFT.mint} 0%, #fff 80%)`
                : `linear-gradient(160deg, ${ACCENT_SOFT.coral} 0%, #fff 80%)`,
              border: `3px solid ${passed ? ACCENT_HEX.mint : ACCENT_HEX.coral}`,
            }}
          >
            <p className="text-4xl mb-2" aria-hidden>
              {passed ? '🌟' : '💪'}
            </p>
            <p className="junior-display text-2xl sm:text-3xl" style={{ color: '#2A2540' }}>
              {score} pts
            </p>
            <p className="text-sm font-extrabold mt-1" style={{ color: passed ? ACCENT_HEX.mint : ACCENT_HEX.coral }}>
              {passed ? '¡Eco perfecto!' : 'Sigue practicando'}
            </p>
            <p className="text-xs font-bold mt-2" style={{ color: '#6B6385' }}>
              ✓{summary.hits} · tarde {summary.late} · ✗{summary.miss}
            </p>

            <div className="flex gap-2 mt-4">
              <button
                type="button"
                onClick={onRetry}
                className="flex-1 py-2.5 rounded-xl font-extrabold text-white text-sm"
                style={{ background: ACCENT_HEX.sun }}
              >
                Reintentar
              </button>
              <button
                type="button"
                onClick={onBackToLobby}
                className="flex-1 py-2.5 rounded-xl font-extrabold text-white text-sm"
                style={{ background: ACCENT_HEX.violet }}
              >
                Configurar
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EcoRitmoStage;
